import React, {useMemo} from 'react'
import clsx from 'clsx'
import {Row, Table} from '@tanstack/react-table'
import {useTranslation} from 'react-i18next'
import {UiTableCellTextPrimary, UiTableCellTextSecondary} from 'components/Ui/UiTableCellText'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'
import UiOfferStatusPill from 'components/Ui/UiOfferStatusPill'
import UiCompanyTypePill from 'components/Ui/UiCompanyTypePill'
import UiPill from 'components/Ui/UiPill'
import UiTableRowActionsContainer from 'components/Ui/UiTableRowActionsContainer'
import UiTableRowActionButton from 'components/Ui/UiTableRowActionButton'
import {UiTableColumnVisibilityToggle} from 'components/Ui/UiTableColumnVisibilityToggle'
import {UI_TABLE_ACTIONS_CELL_CLASS} from 'components/Ui/UiTableSharedStyles'
import Dates from 'utils/Dates'
import {currencyNumberFormat} from 'utils/utility'
import {Offer} from 'types/entities/Offer'
import {OfferListRow} from 'components/leads/EditLeadModalComponents/LeadOffersListTypes'
import { tableActionsMeta } from 'utils/table/tableColumnWidths'

interface UseLeadOffersListColumnsParams {
    onEdit: (offer: Offer) => void
    onDelete: (offer: Offer) => void
}

export const useLeadOffersListColumns = ({ onEdit, onDelete }: UseLeadOffersListColumnsParams) => {
    const { t } = useTranslation()

    return useMemo(
        () => [
            {
                accessorFn: (row: OfferListRow) =>
                    row.rowType === 'calculation' ? row.calculation?.id ?? 0 : row.offer.id,
                id: 'id',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('ID')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return (
                            <UiTableCellTextSecondary className="flex items-center gap-2 pl-4" size="xs">
                                <span className="text-sky-500">↳</span>
                                <span>{currentRow.calculation?.id ?? '-'}</span>
                            </UiTableCellTextSecondary>
                        )
                    }
                    return <UiTableCellTextPrimary>{currentRow.offer.id}</UiTableCellTextPrimary>
                },
            },
            {
                accessorKey: 'guid',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('GUID')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return (
                            <div className="flex items-center gap-2 pl-4">
                                <UiTableCellTextSecondary size="xs">
                                    {currentRow.calculation_label}
                                </UiTableCellTextSecondary>
                                {currentRow.is_target && (
                                    <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                        {t('Target')}
                                    </span>
                                )}
                            </div>
                        )
                    }
                    return <UiTableCellTextPrimary>{currentRow.guid || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorKey: 'company_name',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Company')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return (
                            <UiTableCellTextSecondary className="pl-4" size="xs">
                                {t('Calculation variant')}
                            </UiTableCellTextSecondary>
                        )
                    }
                    return <UiTableCellTextPrimary>{currentRow.company_name || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorKey: 'company_type',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    if (!currentRow.company_type) {
                        return <UiTableCellTextPrimary>-</UiTableCellTextPrimary>
                    }
                    return <UiCompanyTypePill type={currentRow.company_type} />
                },
            },
            {
                accessorKey: 'calculations_count',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Calculations')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <span
                            className={clsx(
                                'inline-flex min-w-8 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold',
                                currentRow.calculations_count > 1
                                    ? 'bg-sky-100 text-sky-700'
                                    : 'bg-gray-100 text-gray-700'
                            )}
                        >
                            {currentRow.calculations_count}
                        </span>
                    )
                },
            },
            {
                accessorKey: 'date',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Event date')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.date ? Dates.date(row.original.date) : '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'duration_breakdown',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Stay')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        <span className={'inline-flex items-center gap-1.5'}>
                            {row.original.duration_karting_hours > 0 && (
                                <UiPill className={'bg-red-100 text-red-700'}>
                                    {row.original.duration_karting_hours}h
                                </UiPill>
                            )}
                            {row.original.duration_non_karting_hours > 0 && (
                                <UiPill className={'bg-yellow-100 text-yellow-800'}>
                                    {row.original.duration_non_karting_hours}h
                                </UiPill>
                            )}
                            {row.original.duration_karting_hours <= 0 &&
                                row.original.duration_non_karting_hours <= 0 && (
                                    <UiPill className={'bg-gray-100 text-gray-700'}>
                                        {row.original.duration_breakdown || '-'}
                                    </UiPill>
                                )}
                        </span>
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'event_time',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Time')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.event_time || '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'persons',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Persons count')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.persons > 0 ? row.original.persons : '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'status',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Status')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return <UiOfferStatusPill status={row.original.offer.status} />
                },
            },
            {
                accessorKey: 'has_reservation',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Has reservation')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <UiTableCellTextPrimary>
                            {row.original.has_reservation === 'yes' ? t('Yes') : t('No')}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'probability',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Probability')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.probability !== undefined && row.original.probability !== null
                            ? `${row.original.probability}%`
                            : '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'valid_until',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Valid until')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <UiTableCellTextPrimary>
                            {row.original.valid_until ? Dates.date(row.original.valid_until) : '-'}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'value',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Revenue')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {currencyNumberFormat(row.original.value ?? 0)}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'offer_number',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Offer #')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.offer_number ?? '---'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'created_at',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Created')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextSecondary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.created_at ? Dates.dateTime(row.original.created_at) : '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorKey: 'updated_at',
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Updated')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OfferListRow> }) => (
                    <UiTableCellTextSecondary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.updated_at ? Dates.dateTime(row.original.updated_at) : '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorKey: 'actions',
                ...tableActionsMeta(2),
                enableSorting: false,
                enableColumnFilter: false,
                header: ({ table }: { table: Table<OfferListRow> }) => (
                    <div className={clsx('flex flex-row items-center leading-tight justify-end h-full')}>
                        <UiTableColumnVisibilityToggle table={table} />
                    </div>
                ),
                cell: ({ row }: { row: Row<OfferListRow> }) => {
                    if (row.original.rowType === 'calculation') return null
                    const offer = row.original.offer
                    return (
                        <UiTableRowActionsContainer className={UI_TABLE_ACTIONS_CELL_CLASS}>
                            <UiTableRowActionButton
                                variant={'edit'}
                                title={t('Edit')}
                                onClick={() => onEdit(offer)}
                            />
                            <UiTableRowActionButton
                                variant={'delete'}
                                title={t('Delete')}
                                onClick={() => onDelete(offer)}
                            />
                        </UiTableRowActionsContainer>
                    )
                },
            },
        ],
        [onEdit, onDelete, t]
    )
}
